import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import api from "@/lib/api";
import {
  ArrowRight, CheckCircle2, ShieldCheck, FileText, Users, GitCompareArrows, ClipboardCheck,
  Gavel, Stamp, ShoppingCart, Truck, MessageSquare, BarChart3, Building2, Lock,
  History, FolderOpen, ScrollText, Factory,
} from "lucide-react";
import RequestDemoModal from "@/components/RequestDemoModal";
import ContactSalesModal from "@/components/ContactSalesModal";
import { Button } from "@/components/ui/button";

const fade = (i) => ({
  initial: { opacity: 0, y: 16 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.45, delay: (i % 4) * 0.08 },
});

const NAVY = "#0A1F4B";
const BLUE = "#1663FF";

function useSalesEmail() {
  const [email, setEmail] = useState("");
  useEffect(() => { api.get("/public/contact").then((r) => setEmail(r.data.sales_email || "")).catch(() => {}); }, []);
  return email;
}

function demoMailto(email) {
  const subject = "ProcDeal Demo Request";
  const body = ["ProcDeal Demo Request", "", "Company Name: ", "Your Name: ", "Work Email: ", "Team Size: ", "What would you like to see: "].join("\n");
  return `mailto:${email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
}

function DemoButton({ onDemo, testid, size = "default", variant = "default", className = "", children }) {
  return (
    <Button size={size} variant={variant} data-testid={testid} onClick={onDemo}
      className={className || "bg-slate-900 hover:bg-slate-800"}>
      {children} <ArrowRight className="ml-1.5 h-4 w-4" />
    </Button>
  );
}

const LIFECYCLE = ["SOURCE", "COMPARE", "EVALUATE", "AUCTION", "APPROVE", "ORDER", "TRACK", "ANALYSE"];

function ProcurementMockup() {
  const offers = [
    { s: "ABC Manufacturing", unit: "₹1,240", total: "₹7,31,600", days: "12 days", badge: "LOWEST", bc: "bg-[#DCFCE7] text-[#166534]" },
    { s: "Sharma Steel Works", unit: "₹1,285", total: "₹7,58,150", days: "9 days", badge: "FASTEST", bc: "bg-[#DBEAFE] text-[#1E40AF]" },
    { s: "Precision Auto", unit: "₹1,310", total: "₹7,72,900", days: "15 days", badge: "", bc: "" },
  ];
  return (
    <div className="overflow-hidden rounded-xl border bg-white shadow-2xl shadow-slate-900/10">
      <div className="flex items-center gap-2 border-b bg-slate-50 px-4 py-2.5">
        <span className="h-2.5 w-2.5 rounded-full bg-red-400" /><span className="h-2.5 w-2.5 rounded-full bg-amber-400" /><span className="h-2.5 w-2.5 rounded-full bg-green-400" />
        <span className="ml-3 font-mono text-xs text-slate-500">app.procdeal.io/comparative-statement</span>
      </div>
      <div className="flex flex-wrap items-center gap-1.5 border-b px-4 py-3">
        {LIFECYCLE.map((s, i) => (
          <span key={s} className="flex items-center gap-1.5">
            <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold tracking-wide ${i < 3 ? "text-white" : "bg-slate-100 text-slate-500"}`}
              style={i < 3 ? { backgroundColor: i === 2 ? BLUE : NAVY } : {}}>{s}</span>
            {i < LIFECYCLE.length - 1 && <span className="text-[10px] text-slate-300">→</span>}
          </span>
        ))}
      </div>
      <div className="p-4">
        <div className="mb-3 flex items-center justify-between">
          <p className="font-heading text-sm font-semibold">Comparative Statement — ENQ-2026-0142</p>
          <span className="flex items-center gap-1.5 rounded-full bg-red-50 px-2 py-0.5 text-[10px] font-semibold text-red-700">
            <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-red-500" /> LIVE AUCTION · Round 2
          </span>
        </div>
        <div className="rounded-md border">
          <div className="grid grid-cols-12 gap-2 border-b bg-slate-50 px-3 py-2 text-[10px] font-medium uppercase tracking-wide text-slate-500">
            <span className="col-span-4">Supplier</span><span className="col-span-2">Unit Price</span>
            <span className="col-span-3">Final Value</span><span className="col-span-2">Delivery</span><span className="col-span-1"></span>
          </div>
          {offers.map((o) => (
            <div key={o.s} className="grid grid-cols-12 items-center gap-2 border-b px-3 py-2.5 last:border-0">
              <span className="col-span-4 truncate text-xs font-medium text-slate-700">{o.s}</span>
              <span className="col-span-2 font-mono text-xs text-slate-600">{o.unit}</span>
              <span className="col-span-3 font-mono text-xs text-slate-600">{o.total}</span>
              <span className="col-span-2 text-xs text-slate-500">{o.days}</span>
              <span className="col-span-1">{o.badge && <span className={`rounded-full px-1.5 py-0.5 text-[9px] font-bold ${o.bc}`}>{o.badge}</span>}</span>
            </div>
          ))}
        </div>
        <div className="mt-3 flex items-center gap-2 rounded-md border border-green-200 bg-green-50 px-3 py-2">
          <CheckCircle2 className="h-4 w-4 text-green-600" />
          <p className="text-xs text-green-800">Technical evaluation complete — 3 of 3 suppliers approved. Approval chain: step 2 of 2 pending.</p>
        </div>
      </div>
    </div>
  );
}

const PAIN_POINTS = [
  "Delayed supplier responses", "Poor visibility into sourcing events", "Manual quotation comparison",
  "Approval bottlenecks", "Repeated supplier follow-ups", "Poor PO visibility",
  "Missed delivery commitments", "Scattered communication", "Limited spend visibility",
];

const LIFECYCLE_STEPS = [
  "Float Enquiry", "Invite Suppliers", "Receive Quotations", "Technical Evaluation", "Compare Offers",
  "Run Auction", "Approval", "Purchase Order", "Delivery Tracking", "Spend Visibility",
];

const FEATURES = [
  { icon: FileText, title: "Smart Enquiries", points: ["Material code, description, quantity, UOM, incoterms", "Delivery timeline, address, attachments", "Buyer Terms & Conditions and comments", "Invite multiple suppliers to one sourcing event"] },
  { icon: Users, title: "Supplier Collaboration", points: ["Suppliers open enquiries and indicate participation", "Submit quotations with GST, payment terms, incoterms", "Technical specifications, packaging & freight costs", "Document uploads and comments"] },
  { icon: GitCompareArrows, title: "Comparative Statement", points: ["Unit price, quantity, base value, GST, final value", "Delivery days, payment & delivery terms", "Technical evaluation outcome per supplier", "Auction results alongside quoted offers"] },
  { icon: ClipboardCheck, title: "Technical Evaluation", points: ["Route quotations to technical reviewers", "Review specifications and supplier attachments", "Approve, reject and comment", "Commercial pricing hidden where workflow restricts it"] },
  { icon: Gavel, title: "Smart Auctions", points: ["Reverse English, Dutch and Japanese auctions", "Auction rounds, live bidding and timelines", "Re-auction when requirements change", "Full results and supplier participation history"] },
  { icon: Stamp, title: "Approval Workflows", points: ["Configurable, sequential approval chains", "Comparative statement and supplier offers in one view", "Technical comments and auction results included", "Approve, reject and comment with full history"] },
  { icon: ShoppingCart, title: "Purchase Order Management", points: ["PO number, line items, material code, description", "Quantity, UOM and value per line", "Expected delivery and status tracking", "PO-level and line-item-level views kept separate"] },
  { icon: Truck, title: "Post-PO Visibility", points: ["Acknowledgement, manufacturing, ready for dispatch", "Transport mode, in-transit, expected delivery", "Delivery modifications, delays, partial deliveries", "Fewer manual supplier follow-ups"] },
  { icon: MessageSquare, title: "Automated Supplier Follow-Ups", points: ["Automated communication via email/WhatsApp architecture", "Suppliers respond through secure action links", "Responses update the workspace automatically", "Complete communication history per PO"] },
  { icon: BarChart3, title: "Spend Visibility", points: ["Total spend by supplier, category, material, department", "PO value, savings and supplier performance", "Procurement trends and method mix", "Exportable reports for leadership reviews"] },
];

const INDUSTRIES = ["Manufacturing", "Auto Components", "Pharma", "Chemicals", "Construction", "EPC", "Engineering", "Industrial Products", "Infrastructure", "Other Procurement-Intensive Businesses"];

const VALUE_CARDS = [
  { title: "Reduce Manual Follow-Ups", text: "Automate supplier communication and reminders." },
  { title: "Improve Procurement Visibility", text: "Track every sourcing event and PO from one workspace." },
  { title: "Make Faster Decisions", text: "Compare supplier offers, technical evaluations and auction results in one place." },
  { title: "Control Spend", text: "Get a connected view of procurement activity and spend." },
];

const HOW_STEPS = [
  ["Create an Enquiry", "Define material requirements, quantity, UOM, delivery timeline and terms."],
  ["Invite Suppliers", "Search and invite multiple suppliers to participate."],
  ["Receive Offers", "Suppliers submit commercial and technical information."],
  ["Evaluate & Compare", "Technical reviewers evaluate specifications while procurement teams compare commercial offers."],
  ["Run an Auction", "Conduct an auction or re-auction when required."],
  ["Get Approval", "Route the event through the configured approval chain."],
  ["Convert to PO", "Create and manage purchase orders."],
  ["Track Delivery", "Monitor acknowledgement, manufacturing, dispatch, transit and delivery."],
  ["Analyse Spend", "Use analytics and spend visibility to improve procurement decisions."],
];

const STAKEHOLDERS = [
  { title: "Buyers", text: "Run procurement without endless follow-ups." },
  { title: "Suppliers", text: "Respond faster and manage multiple buyer relationships from one place." },
  { title: "Technical Reviewers", text: "Evaluate technical compliance without commercial bias." },
  { title: "Approvers", text: "Review comparative commercial and technical information before making decisions." },
  { title: "Procurement Leaders", text: "Get visibility into procurement performance and spend." },
];

const TRUST_ITEMS = [
  { icon: History, label: "Complete Event Flow" }, { icon: ScrollText, label: "Audit Trail" },
  { icon: Lock, label: "Role-Based Access" }, { icon: ShieldCheck, label: "Enterprise Data Isolation" },
  { icon: FolderOpen, label: "Document Management" }, { icon: MessageSquare, label: "Supplier Communication History" },
  { icon: Stamp, label: "Approval History" }, { icon: Gavel, label: "Auction History" },
  { icon: FileText, label: "PO History" },
];

const PLANS = [
  { name: "Basic", price: "₹99,990/year", popular: false, cta: "Get Started", features: ["3 Buyers", "100 Suppliers", "3 Technical Reviewers", "2 Approvers"] },
  { name: "Lite", price: "₹1,99,990/year", popular: true, cta: "Get Started", features: ["10 Buyers", "500 Suppliers", "5 Technical Reviewers", "5 Approvers"] },
  { name: "Custom", price: "Custom Pricing", popular: false, cta: "Contact Sales", features: ["Custom Buyers", "Custom Suppliers", "Custom Reviewers", "Custom Approvers", "Custom Workflows", "Enterprise Integrations"] },
];

export default function Landing() {
  const salesEmail = useSalesEmail();
  const [demoOpen, setDemoOpen] = useState(false);
  const [salesOpen, setSalesOpen] = useState(false);
  return (
    <div className="min-h-screen bg-white">
      <RequestDemoModal open={demoOpen} onOpenChange={setDemoOpen} />
      <ContactSalesModal open={salesOpen} onOpenChange={setSalesOpen} />
      <header className="sticky top-0 z-30 border-b bg-white/90 backdrop-blur">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-6">
          <Link to="/" className="flex items-center gap-2" data-testid="landing-logo">
            <img src="/brand/procdeal-logo.png" alt="ProcDeal" className="h-8 w-auto" />
          </Link>
          <nav className="hidden items-center gap-8 text-sm font-medium text-slate-600 lg:flex">
            <a href="#how" className="transition-colors hover:text-slate-900">How It Works</a>
            <a href="#solution" className="transition-colors hover:text-slate-900">Solutions</a>
            <a href="#features" className="transition-colors hover:text-slate-900">Features</a>
            <a href="#analytics" className="transition-colors hover:text-slate-900">Analytics</a>
            <a href="#pricing" className="transition-colors hover:text-slate-900">Pricing</a>
          </nav>
          <div className="flex items-center gap-2">
            <Link to="/login"><Button variant="ghost" data-testid="landing-login-btn">Log In</Button></Link>
            <DemoButton onDemo={() => setDemoOpen(true)} testid="landing-demo-btn">Request a Demo</DemoButton>
          </div>
        </div>
      </header>

      <section className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-12 px-6 py-20 md:grid-cols-12 md:py-24" data-testid="hero">
        <motion.div {...fade(0)} className="md:col-span-5">
          <span className="inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-semibold tracking-wide" style={{ borderColor: BLUE, color: NAVY, backgroundColor: "#F0F5FF" }}>
            PROCUREMENT MANAGEMENT PLATFORM
          </span>
          <h1 className="mt-5 font-heading text-4xl font-semibold leading-[1.05] tracking-tight sm:text-5xl lg:text-6xl" data-testid="hero-headline">
            Connect. Compare. <span style={{ color: BLUE }}>Close.</span>
          </h1>
          <p className="mt-5 max-w-lg text-base text-slate-600 md:text-lg">
            A smarter way to manage procurement — from supplier enquiries and quotations to auctions, approvals, purchase orders and real-time delivery visibility.
          </p>
          <div className="mt-8 flex flex-wrap items-center gap-3">
            <DemoButton onDemo={() => setDemoOpen(true)} testid="hero-demo-btn" size="lg">Request a Demo</DemoButton>
            <a href="#how"><Button size="lg" variant="outline" data-testid="hero-explore-btn">Explore ProcDeal</Button></a>
          </div>
          <p className="mt-6 text-sm text-slate-500">
            Built for modern procurement teams that want less follow-up, better visibility and faster decisions.
          </p>
          <p className="mt-4 font-heading text-sm font-semibold" style={{ color: NAVY }}>One platform for the complete procurement lifecycle.</p>
        </motion.div>
        <motion.div {...fade(1)} className="md:col-span-7">
          <ProcurementMockup />
        </motion.div>
      </section>

      <section className="border-y py-20" style={{ backgroundColor: "#F7F9FC" }}>
        <div className="mx-auto max-w-7xl px-6">
          <motion.p {...fade(0)} className="text-xs font-semibold uppercase tracking-widest" style={{ color: BLUE }}>Procurement shouldn't require constant follow-up.</motion.p>
          <motion.h2 {...fade(1)} className="mt-3 max-w-3xl font-heading text-3xl font-semibold tracking-tight">
            Your procurement team shouldn't spend its day chasing updates.
          </motion.h2>
          <motion.p {...fade(2)} className="mt-4 max-w-3xl text-base text-slate-600">
            Procurement teams often manage enquiries, supplier responses, quotations, technical evaluations, approvals, purchase orders and deliveries across disconnected emails, spreadsheets, WhatsApp messages and manual follow-ups. This creates:
          </motion.p>
          <div className="mt-8 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {PAIN_POINTS.map((p, i) => (
              <motion.div key={p} {...fade(i)} className="flex items-center gap-2.5 rounded-lg border bg-white px-4 py-3">
                <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-red-400" />
                <p className="text-sm text-slate-700">{p}</p>
              </motion.div>
            ))}
          </div>
          <motion.div {...fade(3)} className="mt-8">
            <a href="#how"><Button data-testid="problem-how-btn" className="bg-slate-900 hover:bg-slate-800">See How ProcDeal Works <ArrowRight className="ml-1.5 h-4 w-4" /></Button></a>
          </motion.div>
        </div>
      </section>

      <section id="solution" className="py-20">
        <div className="mx-auto max-w-7xl px-6">
          <motion.h2 {...fade(0)} className="font-heading text-3xl font-semibold tracking-tight">One procurement workspace. From enquiry to delivery.</motion.h2>
          <motion.p {...fade(1)} className="mt-4 max-w-3xl text-base text-slate-600">
            ProcDeal brings sourcing, supplier collaboration, evaluation, commercial comparison, auctions, approvals and post-PO tracking into one connected platform.
          </motion.p>
          <div className="mt-10 grid grid-cols-2 gap-3 sm:grid-cols-5" data-testid="lifecycle-steps">
            {LIFECYCLE_STEPS.map((s, i) => (
              <motion.div key={s} {...fade(i)} className="relative rounded-lg border bg-white p-4">
                <span className="flex h-7 w-7 items-center justify-center rounded-full text-xs font-bold text-white" style={{ backgroundColor: i % 2 ? BLUE : NAVY }}>{i + 1}</span>
                <p className="mt-3 text-sm font-semibold">{s}</p>
                {i < LIFECYCLE_STEPS.length - 1 && <span className="absolute -right-2.5 top-1/2 hidden -translate-y-1/2 text-slate-300 sm:block">→</span>}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section id="features" className="border-t py-20" style={{ backgroundColor: "#F7F9FC" }}>
        <div className="mx-auto max-w-7xl px-6">
          <motion.p {...fade(0)} className="text-xs font-semibold uppercase tracking-widest" style={{ color: BLUE }}>Everything your procurement team needs</motion.p>
          <div className="mt-8 grid grid-cols-1 gap-6 md:grid-cols-2">
            {FEATURES.map((f, i) => (
              <motion.div key={f.title} {...fade(i)} className="rounded-xl border bg-white p-6 transition-all duration-150 hover:-translate-y-[1px] hover:shadow-md" data-testid={`feature-${f.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg" style={{ backgroundColor: "#F0F5FF" }}>
                    <f.icon className="h-5 w-5" style={{ color: NAVY }} />
                  </div>
                  <h3 className="font-heading text-lg font-semibold">{f.title}</h3>
                </div>
                <ul className="mt-4 space-y-1.5">
                  {f.points.map((p) => (
                    <li key={p} className="flex items-start gap-2 text-sm text-slate-600">
                      <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0" style={{ color: BLUE }} />{p}
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section id="analytics" className="py-16">
        <div className="mx-auto max-w-7xl px-6">
          <div className="rounded-2xl p-8 md:p-10" style={{ backgroundColor: NAVY }}>
            <p className="text-xs font-semibold uppercase tracking-widest" style={{ color: "#7EA6FF" }}>Analytics & Spend Visibility</p>
            <h2 className="mt-3 font-heading text-2xl font-semibold text-white md:text-3xl">Know where every rupee goes.</h2>
            <p className="mt-3 max-w-2xl text-sm text-slate-300">
              Total spend, spend by supplier, category, material and department. Auction savings, supplier performance, cycle times and procurement trends — connected across every sourcing event and purchase order.
            </p>
            <div className="mt-6 flex flex-wrap gap-2">
              {["Total Spend", "By Supplier", "By Category", "By Material", "By Department", "Savings", "Supplier Performance", "Trends"].map((t) => (
                <span key={t} className="rounded-full border border-white/20 px-3 py-1 text-xs font-medium text-slate-200">{t}</span>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="border-t py-20">
        <div className="mx-auto max-w-7xl px-6">
          <motion.p {...fade(0)} className="text-xs font-semibold uppercase tracking-widest" style={{ color: BLUE }}>Built for procurement-led organizations</motion.p>
          <motion.p {...fade(1)} className="mt-3 max-w-3xl text-base text-slate-600">
            ProcDeal is designed for organizations where procurement decisions directly impact cost, working capital, production and delivery.
          </motion.p>
          <div className="mt-8 flex flex-wrap gap-2.5">
            {INDUSTRIES.map((ind, i) => (
              <motion.span key={ind} {...fade(i)} className="flex items-center gap-2 rounded-full border bg-white px-4 py-2 text-sm font-medium text-slate-700">
                <Factory className="h-3.5 w-3.5 text-slate-400" />{ind}
              </motion.span>
            ))}
          </div>
        </div>
      </section>

      <section className="border-t py-20" style={{ backgroundColor: "#F7F9FC" }}>
        <div className="mx-auto max-w-7xl px-6">
          <motion.h2 {...fade(0)} className="font-heading text-3xl font-semibold tracking-tight">Less follow-up. More procurement.</motion.h2>
          <div className="mt-10 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {VALUE_CARDS.map((v, i) => (
              <motion.div key={v.title} {...fade(i)} className="rounded-xl border bg-white p-6">
                <h3 className="font-heading text-base font-semibold uppercase tracking-wide" style={{ color: NAVY }}>{v.title}</h3>
                <p className="mt-3 text-sm text-slate-600">{v.text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section id="how" className="border-t py-20">
        <div className="mx-auto max-w-7xl px-6">
          <motion.h2 {...fade(0)} className="font-heading text-3xl font-semibold tracking-tight">How ProcDeal works</motion.h2>
          <div className="mt-10 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {HOW_STEPS.map(([t, d], i) => (
              <motion.div key={t} {...fade(i)} className="rounded-xl border bg-white p-6">
                <p className="font-mono text-xs font-semibold" style={{ color: BLUE }}>STEP {i + 1}</p>
                <h3 className="mt-2 font-heading text-lg font-semibold">{t}</h3>
                <p className="mt-2 text-sm text-slate-600">{d}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="border-t py-20" style={{ backgroundColor: "#F7F9FC" }}>
        <div className="mx-auto max-w-7xl px-6">
          <motion.p {...fade(0)} className="text-xs font-semibold uppercase tracking-widest" style={{ color: BLUE }}>Built for enterprise procurement teams</motion.p>
          <motion.h2 {...fade(1)} className="mt-3 font-heading text-3xl font-semibold tracking-tight">Give your procurement team their time back.</motion.h2>
          <motion.p {...fade(2)} className="mt-4 max-w-3xl text-base text-slate-600">
            ProcDeal connects every stakeholder involved in procurement — buyers, suppliers, technical reviewers, approvers and procurement leadership — on a single platform.
          </motion.p>
          <div className="mt-10 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-5">
            {STAKEHOLDERS.map((s, i) => (
              <motion.div key={s.title} {...fade(i)} className="rounded-xl border bg-white p-5">
                <h3 className="font-heading text-sm font-semibold uppercase tracking-wide" style={{ color: NAVY }}>{s.title}</h3>
                <p className="mt-2 text-sm text-slate-600">{s.text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="border-t py-20">
        <div className="mx-auto max-w-7xl px-6">
          <motion.h2 {...fade(0)} className="max-w-3xl font-heading text-3xl font-semibold tracking-tight">Every procurement event. Every decision. Every update. Recorded.</motion.h2>
          <motion.p {...fade(1)} className="mt-4 max-w-2xl text-base text-slate-600">
            ProcDeal creates a complete digital trail across the procurement lifecycle.
          </motion.p>
          <div className="mt-10 grid grid-cols-2 gap-3 sm:grid-cols-3">
            {TRUST_ITEMS.map((t, i) => (
              <motion.div key={t.label} {...fade(i)} className="flex items-center gap-3 rounded-lg border bg-white px-4 py-3.5">
                <t.icon className="h-4 w-4 shrink-0" style={{ color: BLUE }} />
                <p className="text-sm font-medium text-slate-700">{t.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section id="pricing" className="border-t py-20" style={{ backgroundColor: "#F7F9FC" }}>
        <div className="mx-auto max-w-6xl px-6">
          <motion.h2 {...fade(0)} className="text-center font-heading text-3xl font-semibold tracking-tight">Plans that scale with your procurement team</motion.h2>
          <div className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-3">
            {PLANS.map((p, i) => (
              <motion.div key={p.name} {...fade(i)}
                className={`relative flex flex-col rounded-xl border bg-white p-7 ${p.popular ? "shadow-lg" : ""}`}
                style={p.popular ? { borderColor: BLUE, boxShadow: `0 0 0 1px ${BLUE}` } : {}}
                data-testid={`landing-plan-${p.name.toLowerCase()}`}>
                {p.popular && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full px-3 py-1 text-[11px] font-bold tracking-wide text-white" style={{ backgroundColor: BLUE }}>MOST POPULAR</span>
                )}
                <h3 className="font-heading text-lg font-semibold">{p.name}</h3>
                <p className="mt-2 font-heading text-2xl font-semibold" style={{ color: NAVY }}>{p.price}</p>
                <ul className="mt-5 flex-1 space-y-2">
                  {p.features.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm text-slate-600">
                      <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" style={{ color: BLUE }} />{f}
                    </li>
                  ))}
                </ul>
                {p.name === "Custom" ? (
                  <Button data-testid={`landing-plan-cta-${p.name.toLowerCase()}`} className="mt-7 w-full bg-slate-900 hover:bg-slate-800"
                    onClick={() => setSalesOpen(true)}>
                    {p.cta} <ArrowRight className="ml-1.5 h-4 w-4" />
                  </Button>
                ) : (
                  <Link to="/register/enterprise">
                    <Button data-testid={`landing-plan-cta-${p.name.toLowerCase()}`} className="mt-7 w-full bg-slate-900 hover:bg-slate-800">{p.cta}</Button>
                  </Link>
                )}
              </motion.div>
            ))}
          </div>
          <p className="mt-6 text-center text-xs text-slate-500">Enterprise accounts purchase and manage subscriptions. Buyer accounts are invited by their Enterprise.</p>
        </div>
      </section>

      <section className="py-20" style={{ backgroundColor: NAVY }}>
        <div className="mx-auto max-w-4xl px-6 text-center">
          <motion.p {...fade(0)} className="text-xs font-semibold uppercase tracking-widest" style={{ color: "#7EA6FF" }}>Ready to change the way your team procures?</motion.p>
          <motion.h2 {...fade(1)} className="mt-4 font-heading text-3xl font-semibold tracking-tight text-white md:text-4xl">
            Connect suppliers. Compare smarter. Close with confidence.
          </motion.h2>
          <motion.p {...fade(2)} className="mt-4 text-base text-slate-300">Bring your entire procurement lifecycle onto one platform.</motion.p>
          <motion.div {...fade(3)} className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <DemoButton onDemo={() => setDemoOpen(true)} testid="final-demo-btn" size="lg" className="text-white hover:opacity-90" >Request a Demo</DemoButton>
            <Link to="/register/enterprise"><Button size="lg" variant="outline" data-testid="final-get-started-btn" className="border-white/30 bg-transparent text-white hover:bg-white/10">Get Started</Button></Link>
          </motion.div>
        </div>
      </section>

      <footer className="border-t bg-white py-14">
        <div className="mx-auto max-w-7xl px-6">
          <div className="grid grid-cols-2 gap-10 md:grid-cols-5">
            <div className="col-span-2">
              <img src="/brand/procdeal-logo.png" alt="ProcDeal" className="h-8 w-auto" />
              <p className="mt-3 font-heading text-sm font-semibold" style={{ color: NAVY }}>Connect. Compare. Close.</p>
              <p className="mt-2 max-w-xs text-sm text-slate-500">One platform for the complete procurement lifecycle.</p>
            </div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Product</p>
              <ul className="mt-3 space-y-2 text-sm text-slate-600">
                {["Enquiries", "Supplier Collaboration", "Comparative Statement", "Auctions", "Approvals", "Purchase Orders", "PO Tracking", "Analytics", "Spend Visibility"].map((l) => (
                  <li key={l}><a href="#features" className="transition-colors hover:text-slate-900">{l}</a></li>
                ))}
              </ul>
            </div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Company</p>
              <ul className="mt-3 space-y-2 text-sm text-slate-600">
                <li><a href="#solution" className="transition-colors hover:text-slate-900">About</a></li>
                <li><a href={salesEmail ? demoMailto(salesEmail) : "/register/enterprise"} className="transition-colors hover:text-slate-900">Contact</a></li>
                <li><button type="button" data-testid="footer-demo-link" onClick={() => setDemoOpen(true)} className="transition-colors hover:text-slate-900">Request a Demo</button></li>
              </ul>
              <p className="mt-6 text-xs font-semibold uppercase tracking-wide text-slate-400">Resources</p>
              <ul className="mt-3 space-y-2 text-sm text-slate-600">
                <li><a href="#how" className="transition-colors hover:text-slate-900">How It Works</a></li>
                <li><a href="#pricing" className="transition-colors hover:text-slate-900">Pricing</a></li>
              </ul>
            </div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Legal</p>
              <ul className="mt-3 space-y-2 text-sm text-slate-600">
                <li><a href="#" className="transition-colors hover:text-slate-900">Privacy Policy</a></li>
                <li><a href="#" className="transition-colors hover:text-slate-900">Terms</a></li>
                <li><a href="#" className="transition-colors hover:text-slate-900">Security</a></li>
              </ul>
            </div>
          </div>
          <div className="mt-12 border-t pt-6 text-center text-xs text-slate-400">© 2026 ProcDeal. Connect. Compare. Close.</div>
        </div>
      </footer>
    </div>
  );
}
